import { autorun, reaction, runInAction, toJS } from 'mobx';
import { createContext, useContext } from 'react';

import { BaseAppModel } from '@/common/models/AppModel';
import { NetworkDataType } from '@/common/models/NetworkDataTransfers';
import { makeSimpleAutoObservable, smartUpdate } from '@/common/utils/mobx/mobx';

import { firebaseDb, waitForFirestore } from '@/common/firebase';
import { Connection } from '@/common/models/Connection';
import { LobbyActionType } from '@/common/models/LobbySessionActions';
import { ToastManager } from '@/common/models/ToastManager';
import { UserDocumentModel } from '@/common/models/UserDocumentModel';
import { uuid } from '@/common/utils/code-gen';
import { getAuth, onAuthStateChanged, User } from 'firebase/auth';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { ControllerNetworkHandler } from './ControllerNetworkHandler';
import { ControllerUserModel } from './ControllerUserModel';
import { ControllerGlobalSettings, HosterGlobalSettings } from '@toilet-paper-games/core';
import { ControllerUIModel } from './ControllerUIModel';
import { GameInfo } from '@/types';

export enum ScreenState {
  LOADING = 'loading',
  LOBBY = 'lobby',
  GAME = 'game',
}

export enum SessionStorageKeys {
  UUID = 'tpg-controller-uuid',
  HOSTER_UUID = 'tpg-controller-hoster-uuid',
}

export enum LocalStorageKeys {
  lastUsedName = 'tpg-controller-last-used-name',
}

export class ControllerAppModel extends BaseAppModel {
  screenState: ScreenState = ScreenState.LOBBY;

  networkHandler: ControllerNetworkHandler;
  uuid: string;
  toastManager: ToastManager = new ToastManager();
  user: ControllerUserModel | null = null;
  connectedUsers: ControllerUserModel[] = [];
  uiModel = new ControllerUIModel(this);
  previewedGame: GameInfo | null = null;

  forceShowExitButton: boolean = false;
  isPlayingGame: boolean = false;

  get wasKicked() {
    return this.networkHandler.wasKicked;
  }

  private _loggedInUser: User | null = null;
  private _loggedInUserDocument: UserDocumentModel | null = null;
  private userDataSubscription: (() => void) | null = null;

  get loggedInUser() {
    return this._loggedInUser;
  }

  set loggedInUser(user: User | null) {
    // TODO: handle user change
    this._loggedInUser = user;
  }

  get userDisplayName(): string | undefined {
    if (!this._loggedInUserDocument?.displayName) {
      console.warn('_loggedInUserDocument not set');
      return undefined;
    }
    return this._loggedInUserDocument.displayName;
  }

  signOut() {
    const auth = getAuth();
    auth.signOut();
  }

  constructor() {
    super();
    makeSimpleAutoObservable(this, {}, { autoBind: true });

    const storedUuid = sessionStorage.getItem(SessionStorageKeys.UUID);
    // const storedUuid = undefined;

    if (storedUuid) {
      this.uuid = storedUuid;
    } else {
      this.uuid = uuid();
      sessionStorage.setItem(SessionStorageKeys.UUID, this.uuid);
    }

    this.networkHandler = new ControllerNetworkHandler(this);

    autorun(() => {
      this.gamesList.dev = this.devMode ? this.devGameUrl : null;
    });

    this.setupNetworkHandler();
    this.handleAuth();
    this.setupReactions();
    this.setupListeners();

    this.setupStorage();

    this.init();
  }

  setupReactions() {
    this.gamesListReaction();
  }

  gamesListReaction() {
    return reaction(
      () => toJS(this.gamesList),
      () => {
        this.loadGames();
      },
      { fireImmediately: true },
    );
  }

  setupStorage() {
    this.fetchControllerGlobalSettings().then((storage) => {
      this.controllerGlobalSettings = storage;
    });
  }

  get globalSettingsKey() {
    return `tpg-controller-global-settings`;
  }

  controllerGlobalSettings: ControllerGlobalSettings | null = null;
  hosterGlobalSettings: HosterGlobalSettings | null = null;

  // This is async for possible change to use firebase later
  async fetchControllerGlobalSettings(): Promise<ControllerGlobalSettings> {
    const content = localStorage.getItem(this.globalSettingsKey);

    if (!content) {
      return {
        soundFxVolume: 0.5,
      };
    }

    return JSON.parse(content);
  }

  async updateControllerGlobalSettings<T extends keyof ControllerGlobalSettings>(
    key: T,
    value: ControllerGlobalSettings[T],
  ) {
    const content = await this.fetchControllerGlobalSettings();
    runInAction(() => {
      content[key] = value;

      localStorage.setItem(this.globalSettingsKey, JSON.stringify(content));
      this.controllerGlobalSettings = content;
    });
  }

  async updateHosterGlobalSettings<T extends keyof HosterGlobalSettings>(
    key: T,
    value: HosterGlobalSettings[T],
  ) {
    if (!this.networkHandler.hostConnection) {
      return;
    }

    this.networkHandler.hostConnection.send({
      type: NetworkDataType.UPDATE_HOSTER_SETTINGS_C2H,
      data: {
        key,
        value,
      } as any,
    });
  }

  setupListeners() {
    this.setupHeartbeatListener();
  }

  setupHeartbeatListener() {
    this.networkHandler.addDataListener((payload) => {
      this.networkHandler.hostConnection?.send({
        type: NetworkDataType.HEARTBEAT_RETURN_C2H,
        data: {
          messageId: payload.data.messageId,
        },
      });
    }, NetworkDataType.HEARTBEAT_H2C);
  }

  async updateDisplayName(displayName: string) {
    if (!this.user) {
      throw new Error('No player');
    }

    this.user.screenName = displayName;
    localStorage.setItem(LocalStorageKeys.lastUsedName, displayName);

    const user = this.loggedInUser;
    if (!user) {
      return;
    }
    console.log('updating display name', displayName);
    
    try {
      const db = await waitForFirestore();
      await updateDoc(doc(db, 'users', user.uid), {
        displayName: displayName,
      });
      
      if (this._loggedInUserDocument) {
        this._loggedInUserDocument.displayName = displayName;
      }
    } catch (error) {
      console.warn('Failed to update display name in firebase', error);
    }
  }

  handleAuth() {
    const auth = getAuth();

    onAuthStateChanged(auth, (user) => {
      this.userDataSubscription?.();
      this.userDataSubscription = null;

      if (user === null) {
        this.loggedInUser = null;
        this._loggedInUserDocument = null;
        return;
      }

      // Set up user data subscription with Firestore
      waitForFirestore()
        .then(db => {
          const userDocRef = doc(db, 'users', user.uid);
          this.userDataSubscription = onSnapshot(userDocRef, async (docSnapshot) => {
            console.log('user data snapshot', docSnapshot);
            if (!docSnapshot.exists()) {
              console.log('Waiting for data...');
              return;
            }

            const userData = docSnapshot.data() as UserDocumentModel;
            this.loggedInUser = user;
            this._loggedInUserDocument = userData;
            this.userDataSubscription?.();
            this.userDataSubscription = null;
          });
        })
        .catch(error => {
          console.error('Firestore is not available - cannot set up user data subscription', error);
          this.loggedInUser = user;
        });
    });

    onAuthStateChanged(auth, (user) => {
      this.loggedInUser = user;
    });
  }

  setupNetworkHandler() {
    this.networkHandler.addDataListener((payload) => {
      if (!this.user) {
        this.user = ControllerUserModel.fromDto(payload.data.user);
      } else {
        this.user.updateFromDto(payload.data.user);
      }

      const existingUsersMap = new Map(
        this.connectedUsers.map((user) => [user.id, user]),
      );

      this.connectedUsers = payload.data.connectedUsers.map((dto) => {
        const existingUser = existingUsersMap.get(dto.id);
        if (existingUser) {
          existingUser.updateFromDto(dto);
          return existingUser;
        }
        return ControllerUserModel.fromDto(dto);
      });

      this.devMode = payload.data.devMode;
      this.devGameUrl = payload.data.devGameUrl;
      this.forceShowExitButton = payload.data.forceShowExitButton;
      this.showHiddenGames = payload.data.showHiddenGames;
      this.selectedGame = payload.data.selectedGame;
      this.isPlayingGame = payload.data.isPlayingGame;

      if (!this.hosterGlobalSettings || !payload.data.hosterGlobalSettings) {
        this.hosterGlobalSettings = payload.data.hosterGlobalSettings;
      } else {
        smartUpdate(this.hosterGlobalSettings, payload.data.hosterGlobalSettings);
      }
    }, NetworkDataType.SESSION_INFO_H2C);

    autorun(() => {
      if (!this.networkHandler.hostConnection || !this.networkHandler.networkReady) {
        return;
      }

      console.log('fetching session info');

      this.networkHandler.hostConnection.send({
        type: NetworkDataType.FETCH_SESSION_INFO_C2H,
        data: {},
      });
    });

    reaction(
      () => this.isPlayingGame,
      (isPlayingGame) => {
        if (isPlayingGame) {
          this.screenState = ScreenState.GAME;

          return;
        }

        if (!isPlayingGame && this.screenState === ScreenState.GAME) {
          this.screenState = ScreenState.LOBBY;

          return;
        }
      },
      {
        fireImmediately: true,
      },
    );
  }

  startGame() {
    if (!this.selectedGame) {
      throw new Error('No game selected');
    }
    this.networkHandler.hostConnection?.send({
      type: NetworkDataType.LOBBY_ACTION,
      data: {
        action: {
          type: LobbyActionType.START_GAME,
          data: undefined,
        },
      },
    });
    this.uiModel.toggleShowGameInfo(false);
  }

  trySelectGame(gameUniqueName: string) {
    const game = this.games.get(gameUniqueName ?? '');
    if (!game) {
      console.error('Game not found', gameUniqueName);
      return false;
    }
    this.previewedGame = game;
    this.uiModel.toggleShowGameInfo(true);
    // TODO: At some point once we have a way to make requests to the host, we should probably return a promise here
    if (!this.user?.isHost) return false;
    this.networkHandler.hostConnection?.send({
      type: NetworkDataType.LOBBY_ACTION,
      data: {
        action: {
          type: LobbyActionType.SELECT_GAME,
          data: { gameUniqueName },
        },
      },
    });
    return false;
  }

  suggestGame(gameUniqueName: string) {
    this.networkHandler.hostConnection?.send({
      type: NetworkDataType.LOBBY_ACTION,
      data: {
        action: {
          type: LobbyActionType.SUGGEST_GAME,
          data: { gameUniqueName },
        },
      },
    });
  }

  promoteUser(id: string) {
    this.networkHandler.hostConnection?.send({
      type: NetworkDataType.LOBBY_ACTION,
      data: {
        action: {
          type: LobbyActionType.PROMOTE_LEADER,
          data: { uuid: id },
        },
      },
    });
  }

  demoteUser(id: string) {
    this.networkHandler.hostConnection?.send({
      type: NetworkDataType.LOBBY_ACTION,
      data: {
        action: {
          type: LobbyActionType.DEMOTE_LEADER,
          data: { uuid: id },
        },
      },
    });
  }

  kickUser(id: string) {
    this.networkHandler.hostConnection?.send({
      type: NetworkDataType.LOBBY_ACTION,
      data: {
        action: {
          type: LobbyActionType.KICK_USER,
          data: { userId: id },
        },
      },
    });
  }
}

export const controllerAppModelContext = createContext<ControllerAppModel | null>(null);
export function useControllerAppModel() {
  const controllerAppModel = useContext(controllerAppModelContext);

  if (!controllerAppModel) {
    throw new Error('controllerAppModelContext not found');
  }

  return controllerAppModel;
}

export function useControllerNetworkHandler(): ControllerNetworkHandler {
  const appModel = useControllerAppModel();

  return appModel.networkHandler;
}

export function useControllerHostConnection(): Connection {
  const networkHandler = useControllerNetworkHandler();

  const hostConnection = networkHandler.hostConnection;

  if (!hostConnection) {
    throw new Error('Host connection not found');
  }

  return hostConnection;
}
